import { createContext, use, useEffect, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiClient, ApiMemberRole, AuthState, createBrowserApiClient } from '../openapi';
import { useConst } from '../hooks/useConst';
import { reportUserId } from '../utils/tracking';

export type AuthContextValue = {
  readonly state: AuthState;
  readonly client: ApiClient;
  readonly role: ApiMemberRole;
  readonly businessId: string | null;
  readonly setBusinessId: (businessId: string | null) => Promise<boolean>;
  readonly setAccessToken: (accessToken: string | null) => void;
  readonly setRefreshToken: (refreshToken: string | null) => void;
};

const AuthContext = createContext<AuthContextValue>({
  state: 'loading',
  client: undefined!,
  role: 'MEMBER',
  businessId: null,
  setBusinessId: undefined!,
  setAccessToken: undefined!,
  setRefreshToken: undefined!,
});

export function AuthProvider({
  baseUrl,
  children,
}: {
  baseUrl: string;
  children: React.ReactNode;
}) {
  const { client, $authState, $role, $businessId, setBusinessId, setAccessToken, setRefreshToken } =
    useConst(() => createBrowserApiClient({ baseUrl }));

  const state = useSyncExternalStore($authState.subscribe, $authState.get) || 'loading';
  const role = useSyncExternalStore($role.subscribe, $role.get);
  const businessId = useSyncExternalStore($businessId.subscribe, $businessId.get);

  const queryClient = useQueryClient();
  useEffect(() => {
    if (state === 'unauthenticated') {
      reportUserId(null);
      queryClient.clear();
    }
  }, [state, queryClient]);

  return (
    <AuthContext.Provider
      value={{ state, client, role, businessId, setBusinessId, setAccessToken, setRefreshToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useApi() {
  return use(AuthContext);
}

export function useApiClient(): ApiClient {
  const { client } = useApi();
  return client;
}

export function useApiState() {
  const { state } = useApi();
  return state;
}

export function useApiRole() {
  const { role } = useApi();
  return role;
}
